import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import EUserType from 'enums/userTypes';
import * as jose from 'jose';
import Cookies from 'js-cookie';
import i18n from 'locales/i18n';
import { call, put } from 'redux-saga/effects';

import { authHttpClient, httpClient, rikHttpClient } from 'services/httpClient';
import ISignInRequestBody from 'services/httpClient/requests/auth/ISignInRequestBody';
import ICheckCookieUuidResponse from 'services/httpClient/responses/auth/ICheckCookieUuidResponse';
import IGetTraderProfileResponse from 'services/httpClient/responses/auth/IGetTraderProfileResponse';

import { getJaimyCookie } from '../../../../../logging/analytics';
import { authActions } from '../index';
import { ISignInRequestAction } from '../reducers/signIn/request';

function* signIn(action: PayloadAction<ISignInRequestAction>) {
  try {
    const body: ISignInRequestBody = {
      email: action.payload.data.email,
      password: action.payload.data.password,
    };

    const { data: accessToken }: AxiosResponse<string> = yield call(
      authHttpClient.post,
      'api/users/login',
      body,
    );

    httpClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    rikHttpClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    yield put(authActions.signInSuccess());
    yield put(
      authActions.setToken({
        accessToken,
      }),
    );

    const logInToken: jose.JaimyJWTPayload = jose.decodeJwt(accessToken);

    i18n.changeLanguage(logInToken.locale);

    // handle jaimyCookie
    getJaimyCookie(logInToken);
    const jaimyCookie =
      Cookies.get('jaimyCookie') &&
      JSON.parse(Cookies.get('jaimyCookie') as string);

    // if uuid of jaimyCookie does not match the one from DB, replace it in the cookie
    if (logInToken.trader_id && jaimyCookie) {
      const res: AxiosResponse<ICheckCookieUuidResponse, any> = yield call(
        httpClient.get,
        `traders/v1/traders/${logInToken.trader_id}/uuid/?uuid=${jaimyCookie.uuid}`,
        {
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
            Authorization: `Bearer ${logInToken}`,
          },
        },
      );
      if (res.data.uuid && res.data.uuid !== jaimyCookie.uuid) {
        const updateUuidFromDb = { ...jaimyCookie, uuid: res.data.uuid };
        Cookies.set('jaimyCookie', JSON.stringify(updateUuidFromDb), {
          expires: 180,
        });
      }
    }

    if (!logInToken.rik_pp_access) {
      action.payload.functions.error({
        response: { data: { error: 'access-denied' } },
      });
      yield put(authActions.logoutRequest());
    }

    // Check if user is owner or worker
    if (logInToken.trader_id) {
      const { data }: AxiosResponse<IGetTraderProfileResponse> = yield call(
        httpClient.get,
        `/traders/v1/traders/${logInToken.trader_id}`,
      );

      const { data: interventionPartnershipData }: AxiosResponse = yield call(
        httpClient.get,
        `/backoffice/v1/intervention_partnerships/${
          data.rik_ip_id || data.b2b_ip_id || data.ea_ip_id
        }`,
      );

      data.role = EUserType.Owner;
      data.all_interventions = true;
      data.company_details = true;
      data.invoice_permission = true;
      data.user_management = true;
      data.trader_id = data.id;

      // set company address
      data.address = `${interventionPartnershipData.address.street} ${interventionPartnershipData.address.number}`;
      data.city = interventionPartnershipData.city;
      data.zip = interventionPartnershipData.zip;
      data.phone = interventionPartnershipData.phone;
      if (interventionPartnershipData.iban || data.iban) {
        const ibanValue = interventionPartnershipData.iban || data.iban;
        data.iban = ibanValue.replaceAll(' ', '');
      }

      yield put(authActions.setProfile(data));
    } else if (logInToken.worker_id) {
      // get worker info
      const { data }: AxiosResponse<IGetTraderProfileResponse> = yield call(
        httpClient.get,
        `/rik/v1/workers/${logInToken.worker_id}`,
      );

      const { data: interventionPartnershipData }: AxiosResponse = yield call(
        httpClient.get,
        `/backoffice/v1/intervention_partnerships/${data.intervention_partnership_id}`,
      );

      // check role of worker
      if (data.admin) {
        data.role = EUserType.Admin;
      } else if (data.accountant) {
        data.role = EUserType.Accountant;
      } else if (data.technician) {
        data.role = EUserType.Technician;
      }

      // set company address
      data.address = `${interventionPartnershipData.address.street} ${interventionPartnershipData.address.number}`;
      data.city = interventionPartnershipData.city;
      data.zip = interventionPartnershipData.zip;
      data.phone = interventionPartnershipData.phone;

      yield put(authActions.setProfile(data));
    }

    action.payload.functions.navigateTo();
  } catch (err: any) {
    action.payload.functions.error(err);
    yield put(authActions.signInFailure());
  }
}

export default signIn;
